var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Icon, Tooltip, TooltipPortal } from "@animech/ui";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import * as Styled from "./grip-position-overlay-content.style";
import { EditButtons, } from "ui/components/elements/edit-buttons/edit-buttons";
import { GripPositionComponentInfo } from "ui/components/panels/canvas/node-components/grip-position/grip-position-component-info";
import { GripPositionSettings } from "ui/components/panels/canvas/node-components/grip-position/grip-position-settings";
import { PaddingLessTooltipClassName } from "ui/app/global.style";
import { selectors, useAppSelector } from "store";
import { useComponentSelect } from "hooks/use-component-select";
import { ComponentSection } from "ui/components/panels/canvas/node-components/component-select/component-section";
export var GripPositionOverlayContent = function (_a) {
    var actions = _a.actions, node = _a.node;
    var _b = __read(useState(), 2), height = _b[0], setHeight = _b[1];
    var _c = __read(useState(), 2), maxHeight = _c[0], setMaxHeight = _c[1];
    var ref = useRef(null);
    var tooltipResizeObserverRef = useRef();
    var updateRef = useRef();
    var tooltipContentRef = useCallback(function (node) {
        var _a;
        (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        if (node) {
            tooltipResizeObserverRef.current = new ResizeObserver(function () {
                var _a;
                (_a = updateRef.current) === null || _a === void 0 ? void 0 : _a.call(updateRef);
            });
            tooltipResizeObserverRef.current.observe(node);
        }
    }, []);
    var updateMaxHeight = useCallback(function () {
        var _a;
        // Use the inner ref to find the wrapper div (parent), since TooltipPortal
        // overwrites the ref on the anchor element.
        var wrapperEl = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (!wrapperEl)
            return;
        var rect = wrapperEl.getBoundingClientRect();
        var viewportHeight = window.innerHeight;
        var margin = 16;
        var available = viewportHeight - rect.top - margin;
        setMaxHeight(Math.max(available, 100));
    }, []);
    var isCentralizedSystem = useAppSelector(selectors.selectIsCentralizedSystem);
    var gripPosition = node.component;
    var t = useTranslation().t;
    var nodes = useMemo(function () { return [node]; }, [node]);
    var _d = __read(useState(), 2), selectedSection = _d[0], setSelectedSection = _d[1];
    var _e = useComponentSelect(nodes, "center", selectedSection), tooltipContent = _e.component, selectedCupOptions = _e.selectedCupOptions, selectedCupMountingOptions = _e.selectedCupMountingOptions, selectedPumpOptions = _e.selectedPumpOptions, cupTechnologyOptions = _e.cupTechnologyOptions, selectedCupTechnologyOptions = _e.selectedCupTechnologyOptions;
    var shouldBeOpenInitially = selectedCupOptions.length === 0;
    var _f = __read(useState(shouldBeOpenInitially), 2), open = _f[0], setOpen = _f[1];
    useEffect(function () {
        var observer = new ResizeObserver(function (_a) {
            var _b = __read(_a, 1), entry = _b[0];
            setHeight(entry.contentRect.height);
        });
        if (ref.current)
            observer.observe(ref.current);
        return function () { return observer.disconnect(); };
    }, []);
    useEffect(function () {
        return function () { var _a; return (_a = tooltipResizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect(); };
    }, []);
    useLayoutEffect(function () {
        updateMaxHeight();
    });
    useEffect(function () {
        var onResize = function () { return updateMaxHeight(); };
        window.addEventListener("resize", onResize);
        return function () { return window.removeEventListener("resize", onResize); };
    }, [updateMaxHeight]);
    return (_jsx(TooltipPortal, { placement: "right", open: selectedSection !== undefined, updateRef: updateRef, anchorElement: 
        // wrapping in div to avoid problems with the ref props passed to GripPositionOverlayContentRoot
        _jsx("div", { style: {
                pointerEvents: "auto",
                maxHeight: maxHeight ? "".concat(maxHeight, "px") : undefined,
                overflowY: maxHeight ? "auto" : undefined,
            }, onWheel: function (e) { return e.stopPropagation(); }, onMouseDown: function (e) { return e.stopPropagation(); }, children: _jsxs(Styled.GripPositionOverlayContentRoot, { ref: ref, children: [_jsx(GripPositionSettings, { gripPosition: gripPosition }), _jsx(EditButtons, { actions: actions }), _jsxs(Styled.GripPositionComponentSelectionRoot, { children: [_jsx(Styled.ChevronContainer, { children: _jsx(Styled.Chevron, { onClick: function () { return setOpen(!open); }, children: _jsx(Icon, { name: open ? "chevron-up" : "chevron-down", color: "primary" }) }) }), _jsxs(Styled.SectionList, { state: open ? "open" : "closed", children: [_jsx(ComponentSection, { header: t("edit_grip_position.select_cup"), selected: selectedSection === "cup-combined", onClick: function () {
                                            return selectedSection !== "cup-combined"
                                                ? setSelectedSection("cup-combined")
                                                : setSelectedSection(undefined);
                                        }, value: selectedCupOptions.length > 0
                                            ? selectedCupOptions[0].label
                                            : undefined }), cupTechnologyOptions.length > 1 && (_jsx(ComponentSection, { header: t("edit_grip_position.select_cup_technology"), selected: selectedSection === "technology", onClick: function () {
                                            return selectedSection !== "technology"
                                                ? setSelectedSection("technology")
                                                : setSelectedSection(undefined);
                                        }, value: selectedCupTechnologyOptions.length > 0
                                            ? selectedCupTechnologyOptions[0].label
                                            : undefined })), _jsx(ComponentSection, { header: t("edit_grip_position.select_cup_mounting"), selected: selectedSection === "mounting", onClick: function () {
                                            return selectedSection !== "mounting"
                                                ? setSelectedSection("mounting")
                                                : setSelectedSection(undefined);
                                        }, value: selectedCupMountingOptions.length > 0
                                            ? selectedCupMountingOptions[0].label
                                            : undefined }), !isCentralizedSystem && (_jsx(ComponentSection, { header: t("edit_grip_position.select_pump"), selected: selectedSection === "pump", onClick: function () {
                                            return selectedSection !== "pump"
                                                ? setSelectedSection("pump")
                                                : setSelectedSection(undefined);
                                        }, value: selectedPumpOptions.length > 0
                                            ? selectedPumpOptions[0].label
                                            : undefined }))] })] }), _jsx(GripPositionComponentInfo, { gripPosition: gripPosition, selectedCupLabel: selectedCupOptions.length > 0
                            ? selectedCupOptions[0].label
                            : undefined, selectedPumpLabel: selectedPumpOptions.length > 0
                            ? selectedPumpOptions[0].label
                            : undefined })] }) }), children: _jsx(Tooltip, { className: PaddingLessTooltipClassName, color: "neutral", children: _jsx(Styled.TooltipContent, { ref: tooltipContentRef, style: { minHeight: height }, children: tooltipContent }) }) }));
};
